import { useEffect, useMemo, useState } from "react";
import {
  getTrainingSessionMetricHistory,
  getTrainingSessionMetrics,
  type TrainingSessionMetricHistoryView,
  type TrainingSessionMetricsView,
} from "@/api";
import { type LoadState, resolveLoadErrorMessage } from "@/components/dataset";

// 곡선 갱신 주기. training 쪽 metric 캐시가 30s SWR 이라 더 짧게 폴링해도 더 최신 값을 받지 못한다.
// 로그(3s)와 달리 지표는 천천히 쌓이므로 15s 면 충분하다.
const metricsPollMs = 15_000;

// 체크포인트 저장 시점 join 과 best 판정의 소스. 선택 지표와 무관하게 항상 받는다.
export const alwaysFetchedMetricKey = "validation_loss";

export type SessionMetricsController = {
  metricsState: LoadState<TrainingSessionMetricsView>;
  histories: Record<string, LoadState<TrainingSessionMetricHistoryView>>;
  refetch: () => void;
};

/**
 * 학습 지표(key 목록 + 선택 지표 시계열) 로더. 분석 및 평가 탭과 체크포인트 탭이 공유한다.
 *
 * - `enabled` 가 꺼지면(탭 이탈·창 최소화) 폴링을 멈추고, 이미 받은 데이터는 유지한다.
 * - `live`(세션 진행 중)면 15s 체인드 setTimeout 으로 갱신한다. `live` 가 꺼지는 전이(terminal)에
 *   effect 가 다시 실행되어 마지막 1회를 받고 멈춘다.
 * - 시계열은 선택 지표만 병렬로 받는다. 폴링 갱신 중에는 성공 데이터를 유지한 채 교체한다.
 *   그래서 로딩 표시가 깜빡이지 않는다.
 */
export function useSessionMetrics(
  sessionId: string,
  options: { enabled: boolean; live: boolean; selectedKeys: readonly string[] },
): SessionMetricsController {
  const { enabled, live, selectedKeys } = options;
  const [metricsState, setMetricsState] = useState<LoadState<TrainingSessionMetricsView>>({
    status: "idle",
  });
  const [histories, setHistories] = useState<
    Record<string, LoadState<TrainingSessionMetricHistoryView>>
  >({});
  const [refetchTick, setRefetchTick] = useState(0);

  // 키 배열 identity 가 바뀌어도 effect 가 다시 시작하지 않도록 정렬한 문자열로 줄인다.
  const wantedKeysKey = useMemo(
    () => [...new Set([...selectedKeys, alwaysFetchedMetricKey])].sort().join("\n"),
    [selectedKeys],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const wantedKeys = wantedKeysKey.split("\n");
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function load(): Promise<void> {
      // 폴링 갱신은 기존 성공 데이터를 유지한다. 첫 로드만 로딩으로 보인다.
      setMetricsState((prev) => (prev.status === "success" ? prev : { status: "loading" }));
      setHistories((prev) => {
        const next = { ...prev };
        for (const key of wantedKeys) {
          if (next[key]?.status !== "success") {
            next[key] = { status: "loading" };
          }
        }
        return next;
      });

      const [metricsResult, ...historyResults] = await Promise.allSettled([
        getTrainingSessionMetrics(sessionId),
        ...wantedKeys.map((key) => getTrainingSessionMetricHistory(sessionId, key)),
      ]);
      if (cancelled) {
        return;
      }
      setMetricsState(
        metricsResult.status === "fulfilled"
          ? { status: "success", data: metricsResult.value }
          : { status: "error", message: resolveLoadErrorMessage(metricsResult.reason) },
      );
      setHistories((prev) => {
        const next = { ...prev };
        wantedKeys.forEach((key, index) => {
          const result = historyResults[index];
          next[key] =
            result.status === "fulfilled"
              ? { status: "success", data: result.value }
              : { status: "error", message: resolveLoadErrorMessage(result.reason) };
        });
        return next;
      });
      if (live) {
        timer = setTimeout(() => void load(), metricsPollMs);
      }
    }

    void load();
    return () => {
      cancelled = true;
      if (timer !== null) {
        clearTimeout(timer);
      }
    };
  }, [sessionId, enabled, live, wantedKeysKey, refetchTick]);

  return {
    metricsState,
    histories,
    refetch: () => setRefetchTick((tick) => tick + 1),
  };
}
